/**
 * useKonamiCode - listens for the classic ArrowUp ArrowUp ArrowDown ArrowDown
 * ArrowLeft ArrowRight ArrowLeft ArrowRight b a sequence and fires a callback.
 *
 * Case-insensitive. Ignores keystrokes while focus is in an input, textarea,
 * select, or any contenteditable element (so typing "ba" in a form field
 * never fires it).
 *
 * Matches the last N keys pressed (a rolling buffer), not a progress index.
 * This is the same approach as app-sbs's KonamiToPlay (#2467, PR #2813): a
 * progress-index matcher resets wrong on a repeated key. For example, typing
 * an extra ArrowUp (up up up down down left right left right b a) never
 * fired, because the 3rd "up" reset progress to 1 instead of keeping it at 2.
 * A rolling buffer has no such edge case - it only checks whether the last
 * 10 keys equal the sequence.
 */

import { useEffect, useRef } from 'react';

const KONAMI_SEQUENCE = [
  'arrowup',
  'arrowup',
  'arrowdown',
  'arrowdown',
  'arrowleft',
  'arrowright',
  'arrowleft',
  'arrowright',
  'b',
  'a',
];

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export function useKonamiCode(onTrigger: () => void): void {
  const recentRef = useRef<string[]>([]);
  const onTriggerRef = useRef(onTrigger);
  onTriggerRef.current = onTrigger;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;

      const recent = [...recentRef.current, event.key.toLowerCase()].slice(-KONAMI_SEQUENCE.length);
      recentRef.current = recent;

      if (recent.length === KONAMI_SEQUENCE.length && recent.every((key, i) => key === KONAMI_SEQUENCE[i])) {
        recentRef.current = [];
        onTriggerRef.current();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
}
